import { useEffect, useRef, useState, cloneElement, Children, isValidElement } from 'react';

/**
 * Generic dropdown wrapper.
 *   <NavDropdown trigger={<button>…</button>} aria-label="…">
 *     <button>Item 1</button>
 *     <button>Item 2</button>
 *   </NavDropdown>
 *
 * Open on trigger click; close on Esc, click-outside, or clicking any child.
 * The trigger's onClick is composed with the toggle handler.
 */
export default function NavDropdown({ trigger, children, panelClassName, ...ariaProps }) {
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);

    const toggle = () => setOpen((o) => !o);
    const close = () => setOpen(false);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') close(); };
        const onClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) close();
        };
        document.addEventListener('keydown', onKey);
        document.addEventListener('mousedown', onClickOutside);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('mousedown', onClickOutside);
        };
    }, [open]);

    // Wrap the trigger so we can attach toggle + aria
    const wrappedTrigger = isValidElement(trigger)
        ? cloneElement(trigger, {
              onClick: (e) => {
                  trigger.props.onClick?.(e);
                  toggle();
              },
              'aria-expanded': open,
              'aria-haspopup': 'menu',
          })
        : trigger;

    // Close on any item click
    const wrappedChildren = open
        ? Children.map(children, (child) => {
              if (!isValidElement(child)) return child;
              return cloneElement(child, {
                  onClick: (e) => {
                      child.props.onClick?.(e);
                      close();
                  },
              });
          })
        : null;

    return (
        <div ref={containerRef} className="nav-dropdown">
            {wrappedTrigger}
            {open && (
                <div className={`nav-dropdown-panel ${panelClassName || ''}`} role="menu" {...ariaProps}>
                    {wrappedChildren}
                </div>
            )}
        </div>
    );
}
